import { useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import type { ISensemakerOverviewCampaignCard } from '@brushfire/types'
import Box from '@mui/material/Box'
import Collapse from '@mui/material/Collapse'
import Link from '@mui/material/Link'
import Typography from '@mui/material/Typography'
import { RiArrowDownSLine } from 'react-icons/ri'
import {
  SENSEMAKER_OVERVIEW_CAMPAIGN_CARD_BORDER,
  SENSEMAKER_OVERVIEW_CAMPAIGN_CARD_COLLAPSED_HEADER_HEIGHT_PX,
  SENSEMAKER_OVERVIEW_CAMPAIGN_CARD_COLLAPSED_HEIGHT_PX,
  SENSEMAKER_OVERVIEW_CAMPAIGN_CARD_COLLAPSED_PADDING_BOTTOM_PX,
  SENSEMAKER_OVERVIEW_CAMPAIGN_CARD_COLLAPSED_PADDING_TOP_PX,
  SENSEMAKER_OVERVIEW_CAMPAIGN_CARD_COLLAPSED_PADDING_X_PX,
  SENSEMAKER_OVERVIEW_CAMPAIGN_CARD_GAP_PX,
  SENSEMAKER_OVERVIEW_CAMPAIGN_CARD_HEADER_PADDING_Y_PX,
  SENSEMAKER_OVERVIEW_CAMPAIGN_CARD_PADDING_X_PX,
  SENSEMAKER_OVERVIEW_CAMPAIGN_CARD_RADIUS_PX,
  SENSEMAKER_OVERVIEW_CAMPAIGN_CREATED_COLOR,
  SENSEMAKER_OVERVIEW_CAMPAIGN_CREATED_LINE_HEIGHT_PX,
  SENSEMAKER_OVERVIEW_CAMPAIGN_CREATED_SIZE_PX,
  SENSEMAKER_OVERVIEW_CAMPAIGN_HEADER_TO_LOI_GAP_PX,
  SENSEMAKER_OVERVIEW_CAMPAIGN_STATUS_BADGE_HEIGHT_PX,
  SENSEMAKER_OVERVIEW_CAMPAIGN_STATUS_STYLES,
  SENSEMAKER_OVERVIEW_CAMPAIGN_TITLE_COLOR,
  SENSEMAKER_OVERVIEW_CAMPAIGN_TITLE_LETTER_SPACING_PX,
  SENSEMAKER_OVERVIEW_CAMPAIGN_TITLE_LINE_HEIGHT_PX,
  SENSEMAKER_OVERVIEW_CAMPAIGN_TITLE_SIZE_PX,
  SENSEMAKER_OVERVIEW_CAMPAIGN_TITLE_TO_CREATED_GAP_PX,
  SENSEMAKER_OVERVIEW_CAMPAIGNS_HEADER_TO_LIST_GAP_PX,
  sensemakerOverviewSectionTitleSx,
} from '../../lib/sensemaker-overview-layout'
import { formatOverviewDateTime, formatCampaignStatusLabel } from '../../lib/sensemaker-overview-format'
import { SensemakerOverviewLoiSubCard } from './SensemakerOverviewLoiSubCard'

interface Props {
  campaigns: ISensemakerOverviewCampaignCard[]
}

function CampaignsSectionHeader() {
  return (
    <Typography
      component="h2"
      sx={{
        ...sensemakerOverviewSectionTitleSx,
        mb: `${SENSEMAKER_OVERVIEW_CAMPAIGNS_HEADER_TO_LIST_GAP_PX}px`,
      }}
      data-testid="sensemaker-overview-campaigns-header"
    >
      Campaigns
    </Typography>
  )
}

function CampaignMasterCard({ campaign }: Readonly<{ campaign: ISensemakerOverviewCampaignCard }>) {
  const [expanded, setExpanded] = useState(false)
  const statusStyle =
    SENSEMAKER_OVERVIEW_CAMPAIGN_STATUS_STYLES[campaign.status] ??
    SENSEMAKER_OVERVIEW_CAMPAIGN_STATUS_STYLES.draft

  const toggleExpanded = () => setExpanded((v) => !v)

  return (
    <Box
      sx={{
        border: SENSEMAKER_OVERVIEW_CAMPAIGN_CARD_BORDER,
        borderRadius: `${SENSEMAKER_OVERVIEW_CAMPAIGN_CARD_RADIUS_PX}px`,
        mb: `${SENSEMAKER_OVERVIEW_CAMPAIGN_CARD_GAP_PX}px`,
        overflow: 'hidden',
        bgcolor: '#fff',
        ...(expanded
          ? {}
          : {
              height: `${SENSEMAKER_OVERVIEW_CAMPAIGN_CARD_COLLAPSED_HEIGHT_PX}px`,
              minHeight: `${SENSEMAKER_OVERVIEW_CAMPAIGN_CARD_COLLAPSED_HEIGHT_PX}px`,
            }),
      }}
      data-testid={`sensemaker-campaign-card-${campaign.campaignId}`}
      data-collapsed={expanded ? 'false' : 'true'}
    >
      <Box
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onClick={toggleExpanded}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            toggleExpanded()
          }
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          px: expanded
            ? `${SENSEMAKER_OVERVIEW_CAMPAIGN_CARD_PADDING_X_PX}px`
            : `${SENSEMAKER_OVERVIEW_CAMPAIGN_CARD_COLLAPSED_PADDING_X_PX}px`,
          pt: expanded
            ? `${SENSEMAKER_OVERVIEW_CAMPAIGN_CARD_HEADER_PADDING_Y_PX}px`
            : `${SENSEMAKER_OVERVIEW_CAMPAIGN_CARD_COLLAPSED_PADDING_TOP_PX}px`,
          pb: expanded
            ? `${SENSEMAKER_OVERVIEW_CAMPAIGN_CARD_HEADER_PADDING_Y_PX}px`
            : `${SENSEMAKER_OVERVIEW_CAMPAIGN_CARD_COLLAPSED_PADDING_BOTTOM_PX}px`,
          minHeight: expanded
            ? undefined
            : `${SENSEMAKER_OVERVIEW_CAMPAIGN_CARD_COLLAPSED_HEADER_HEIGHT_PX}px`,
          cursor: 'pointer',
          boxSizing: 'border-box',
        }}
      >
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <Link
              component={RouterLink}
              to={`/campaigns/${campaign.campaignId}`}
              underline="hover"
              onClick={(e) => e.stopPropagation()}
              data-testid={`sensemaker-campaign-detail-link-${campaign.campaignId}`}
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontSize: `${SENSEMAKER_OVERVIEW_CAMPAIGN_TITLE_SIZE_PX}px`,
                fontWeight: 600,
                lineHeight: `${SENSEMAKER_OVERVIEW_CAMPAIGN_TITLE_LINE_HEIGHT_PX}px`,
                letterSpacing: `${SENSEMAKER_OVERVIEW_CAMPAIGN_TITLE_LETTER_SPACING_PX}px`,
                color: SENSEMAKER_OVERVIEW_CAMPAIGN_TITLE_COLOR,
              }}
            >
              {campaign.name}
            </Link>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: `${SENSEMAKER_OVERVIEW_CAMPAIGN_STATUS_BADGE_HEIGHT_PX}px`,
                px: '11px',
                borderRadius: '999px',
                bgcolor: statusStyle.bg,
                border: `1px solid ${statusStyle.border}`,
                color: statusStyle.color,
                fontFamily: 'Inter, sans-serif',
                fontSize: '12px',
                fontWeight: 500,
                lineHeight: '16px',
              }}
            >
              {formatCampaignStatusLabel(campaign.status)}
            </Box>
          </Box>
          <Typography
            sx={{
              mt: `${SENSEMAKER_OVERVIEW_CAMPAIGN_TITLE_TO_CREATED_GAP_PX}px`,
              fontFamily: 'Inter, sans-serif',
              fontSize: `${SENSEMAKER_OVERVIEW_CAMPAIGN_CREATED_SIZE_PX}px`,
              fontWeight: 400,
              lineHeight: `${SENSEMAKER_OVERVIEW_CAMPAIGN_CREATED_LINE_HEIGHT_PX}px`,
              color: SENSEMAKER_OVERVIEW_CAMPAIGN_CREATED_COLOR,
            }}
          >
            Created:&nbsp;{formatOverviewDateTime(campaign.createdAt)}
          </Typography>
        </Box>
        <Box
          sx={{
            color: SENSEMAKER_OVERVIEW_CAMPAIGN_TITLE_COLOR,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s',
          }}
          aria-hidden
        >
          <RiArrowDownSLine size={24} />
        </Box>
      </Box>
      <Collapse in={expanded}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: `${SENSEMAKER_OVERVIEW_CAMPAIGN_CARD_GAP_PX}px`,
            px: `${SENSEMAKER_OVERVIEW_CAMPAIGN_CARD_PADDING_X_PX}px`,
            pb: `${SENSEMAKER_OVERVIEW_CAMPAIGN_CARD_PADDING_X_PX}px`,
            pt: `${SENSEMAKER_OVERVIEW_CAMPAIGN_HEADER_TO_LOI_GAP_PX}px`,
          }}
        >
          {campaign.lois.map((loi) => (
            <SensemakerOverviewLoiSubCard
              key={loi.loiId}
              title={loi.title}
              instrumentCount={loi.instrumentCount}
              totalResponses={loi.totalResponses}
              totalRewardDistributedUsd={loi.totalRewardDistributedUsd}
              instrumentTypePill={loi.instrumentTypePill}
              instruments={loi.instruments}
            />
          ))}
        </Box>
      </Collapse>
    </Box>
  )
}

export function SensemakerOverviewCampaignsSection({ campaigns }: Readonly<Props>) {
  if (campaigns.length === 0) {
    return (
      <Box data-testid="sensemaker-overview-campaigns-empty">
        <CampaignsSectionHeader />
        <Typography
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontSize: '14px',
            lineHeight: '20px',
            color: '#6b7a90',
          }}
        >
          No campaigns yet for this Sensemaker.
        </Typography>
      </Box>
    )
  }

  return (
    <Box data-testid="sensemaker-overview-campaigns">
      <CampaignsSectionHeader />
      {campaigns.map((campaign) => (
        <CampaignMasterCard key={campaign.campaignId} campaign={campaign} />
      ))}
    </Box>
  )
}
